import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import moment from 'moment';
import toast from "react-hot-toast";
import { Helpers } from '../../helpers/Helpers';
import { KTCard } from '../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../_metronic/helpers/components/KTCardBody';
import Content from '../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../_metronic/layout/core';
import { ToolbarWrapper } from '../../_metronic/layout/components/toolbar';
import { storeActivityIndicator } from '../../redux/reducers/authSlice';
import RefreshButton from '../../components/RefreshButton';
import ExcelExport from '../../components/ExcelExport';
import { AylikPuantaj, PuantajGunu, PuantajService } from '../../services/PuantajService';
import { saatDakika } from '../../services/MesaiKaydiService';

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const GUN_KISA = ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'];

const RENK = {
    haftaSonu: '#F1F1F4',
    ozelGun: '#FFF8DD',
    eksik: '#FFEEF3',
    ekMesai: '#7239EA',
    eksikYazi: '#F8285A',
    ucret: '#17C653',
};

/** Hafta günü tarihten hesaplanır (backend numaralandırmasına bağlı kalmamak için): 0 Pazar … 6 Cumartesi. */
const haftaGunu = (g: PuantajGunu) => moment(g.tarih.substring(0, 10), 'YYYY-MM-DD').day();

const gunArkaPlani = (g: PuantajGunu | undefined) => {
    if (!g)
        return undefined;

    if (g.ozelGun)
        return RENK.ozelGun;

    var w = haftaGunu(g);
    return w == 0 || w == 6 ? RENK.haftaSonu : undefined;
}

/** Gün hücresi kodları (sunucu: MesaiManager.Puantaj). */
const KOD_ADLARI: Record<string, string> = { L: 'Lojistik', A: 'Ambalaj', O: 'Ofis', V: 'Araç', E: 'Eksik çıkış' };

const STICKY: React.CSSProperties = { position: 'sticky', left: 0, zIndex: 2, background: '#fff', minWidth: 180, borderRight: '1px solid #F1F1F4' };
const HUCRE: React.CSSProperties = { minWidth: 34, padding: '4px 2px', textAlign: 'center', lineHeight: 1.1 };
const TOPLAM: React.CSSProperties = { whiteSpace: 'nowrap', textAlign: 'right', paddingLeft: 10, paddingRight: 10 };

/**
 * Aylık Puantaj (İnsan Kaynakları > Aylık Puantaj, QR Mesai Faz 1b, 11.10.2026).
 * Personel × gün tablosu: L Lojistik, A Ambalaj, E eksik çıkış; mor küçük sayı ek mesai (s:dd, dakika ile).
 * Saatlik ücret ve ek mesai ücreti yalnız "Personel Ücretleri" yetkisine gelir; gelmezse kolonlar gösterilmez.
 */
const Puantaj = () => {
    const dispatch = useDispatch();
    const pageName = "İnsan Kaynakları > Aylık Puantaj"

    const [yil, setYil] = useState<number>(moment().year());
    const [ay, setAy] = useState<number>(moment().month() + 1);
    const [data, setData] = useState<AylikPuantaj>({ gunler: [], personeller: [] });

    useEffect(() => {
        getData();
    }, [yil, ay])

    const getData = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await PuantajService.aylik(yil, ay);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setData({ gunler: response.data?.gunler ?? [], personeller: response.data?.personeller ?? [] });
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const excel = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await PuantajService.excel(yil, ay);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            window.location.href = Helpers.getEndpoint() + "/Excels/" + response.data;
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const gunler = data.gunler;
    const personeller = data.personeller;
    const saatlikVar = personeller.some(p => p.saatlikUcret != null);
    const ucretVar = personeller.some(p => p.toplam?.ekMesaiUcreti != null);

    const genel = personeller.reduce((t, p) => ({
        gun: t.gun + (p.toplam?.calisilanGun ?? 0),
        normal: t.normal + (p.toplam?.normalDk ?? 0),
        lojistik: t.lojistik + (p.toplam?.ekDkLojistik ?? 0),
        ambalaj: t.ambalaj + (p.toplam?.ekDkAmbalaj ?? 0),
        diger: t.diger + (p.toplam?.ekDkDiger ?? 0),
        eksik: t.eksik + (p.toplam?.eksikCikis ?? 0),
        ucret: t.ucret + (p.toplam?.ekMesaiUcreti ?? 0),
    }), { gun: 0, normal: 0, lojistik: 0, ambalaj: 0, diger: 0, eksik: 0, ucret: 0 });

    const buYil = moment().year();
    const yillar = [buYil - 2, buYil - 1, buYil, buYil + 1];

    return (
        <React.Fragment>
            <ToolbarWrapper filters={<React.Fragment>
                <RefreshButton onClick={() => getData()} />

                <div style={{ flex: 1 }}></div>

                <div className='m-0 d-flex align-items-center'>
                    <select className='form-select form-select-sm form-select-solid me-2' style={{ width: 130 }} title='Ay' value={ay} onChange={(e) => setAy(Number(e.target.value))}>
                        {AYLAR.map((a, i) => <option key={i} value={i + 1}>{a}</option>)}
                    </select>

                    <select className='form-select form-select-sm form-select-solid' style={{ width: 100 }} title='Yıl' value={yil} onChange={(e) => setYil(Number(e.target.value))}>
                        {(yillar.includes(yil) ? yillar : [...yillar, yil].sort()).map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>

                <ExcelExport pageName={pageName} excelExport={excel} />
            </React.Fragment>} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                <KTCard className='mb-3'>
                    <KTCardBody className='py-4'>
                        <h4 className="mb-5 text-black">{AYLAR[ay - 1]} {yil}</h4>

                        <div className='table-responsive'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table' style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th style={STICKY}>Personel</th>

                                        {
                                            gunler.map(g =>
                                                <th key={g.gun} style={{ ...HUCRE, background: gunArkaPlani(g) }} title={g.ozelGun ? g.ozelGun.ad : undefined}>
                                                    {g.gun}
                                                    <br />
                                                    <span style={{ fontWeight: 500, textTransform: 'none' }}>{GUN_KISA[haftaGunu(g)]}</span>
                                                </th>
                                            )
                                        }

                                        <th style={TOPLAM}>Gün</th>
                                        <th style={TOPLAM}>Normal</th>
                                        <th style={TOPLAM}>Ek · Lojistik</th>
                                        <th style={TOPLAM}>Ek · Ambalaj</th>
                                        <th style={TOPLAM} title='Ofis ve araç QR&apos;ıyla yapılan ek mesai'>Ek · Diğer (Ofis / Araç)</th>
                                        <th style={TOPLAM}>Eksik</th>
                                        {saatlikVar && <th style={TOPLAM}>Saatlik</th>}
                                        {ucretVar && <th style={TOPLAM}>Ek Mesai Ücreti</th>}
                                    </tr>
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        personeller.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        personeller.map(p =>
                                            <tr key={p.personnelId}>
                                                <td style={STICKY}>
                                                    <span className='text-gray-800'>{p.adSoyad}</span>
                                                    {p.gorevi && <React.Fragment><br /><span className='text-muted fs-8'>{p.gorevi}</span></React.Fragment>}
                                                </td>

                                                {
                                                    gunler.map(g => {
                                                        var h = (p.gunler ?? []).find(x => x.gun == g.gun);
                                                        var eksik = h?.kod == 'E';

                                                        return (
                                                            <td key={g.gun} style={{ ...HUCRE, background: eksik ? RENK.eksik : gunArkaPlani(g) }} title={h?.kod ? `${g.gun}: ${KOD_ADLARI[h.kod] ?? 'Ofis / araç'} · normal ${saatDakika(h.normalDk)} · ek ${saatDakika(h.ekDk)}` : undefined}>
                                                                {h?.kod && <span style={{ fontWeight: 700, color: eksik ? RENK.eksikYazi : undefined }}>{h.kod}</span>}
                                                                {h?.kod && h.kod != 'E' && h.ekDk > 0 && <React.Fragment><br /><span style={{ fontSize: 10, color: RENK.ekMesai }}>{saatDakika(h.ekDk)}</span></React.Fragment>}
                                                            </td>
                                                        )
                                                    })
                                                }

                                                <td style={TOPLAM}>{p.toplam?.calisilanGun ?? 0}</td>
                                                <td style={TOPLAM}>{saatDakika(p.toplam?.normalDk ?? 0)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(p.toplam?.ekDkLojistik ?? 0)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(p.toplam?.ekDkAmbalaj ?? 0)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(p.toplam?.ekDkDiger ?? 0)}</td>
                                                <td style={{ ...TOPLAM, color: (p.toplam?.eksikCikis ?? 0) > 0 ? RENK.eksikYazi : undefined }}>{p.toplam?.eksikCikis ? p.toplam.eksikCikis : '—'}</td>
                                                {saatlikVar && <td style={TOPLAM}>{p.saatlikUcret == null ? '' : Helpers.correctPrice(p.saatlikUcret, 2)}</td>}
                                                {ucretVar && <td style={{ ...TOPLAM, color: RENK.ucret }}>{p.toplam?.ekMesaiUcreti == null ? '' : Helpers.correctPrice(p.toplam.ekMesaiUcreti, 2)}</td>}
                                            </tr>
                                        )
                                    }
                                </tbody>
                                {
                                    personeller.length > 0 && (
                                        <tfoot>
                                            <tr className="ebbe-dip-toplam fw-bolder">
                                                <td style={STICKY}>Toplam</td>
                                                {gunler.map(g => <td key={g.gun} style={{ ...HUCRE, background: gunArkaPlani(g) }}></td>)}
                                                <td style={TOPLAM}>{genel.gun}</td>
                                                <td style={TOPLAM}>{saatDakika(genel.normal)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(genel.lojistik)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(genel.ambalaj)}</td>
                                                <td style={{ ...TOPLAM, color: RENK.ekMesai }}>{saatDakika(genel.diger)}</td>
                                                <td style={TOPLAM}>{genel.eksik || '—'}</td>
                                                {saatlikVar && <td style={TOPLAM}></td>}
                                                {ucretVar && <td style={{ ...TOPLAM, color: RENK.ucret }}>{Helpers.correctPrice(genel.ucret, 2)}</td>}
                                            </tr>
                                        </tfoot>
                                    )
                                }
                            </table>
                        </div>

                        <div className="text-muted fs-7 mt-3">
                            <b>L</b> Lojistik · <b>A</b> Ambalaj · <b>O</b> Ofis · <b>V</b> Araç · <b style={{ color: RENK.eksikYazi }}>E</b> eksik çıkış · <span style={{ color: RENK.ekMesai }}>mor sayı</span> ek mesai (s:dd, dakika ile, yuvarlama yok) ·
                            {' '}<span style={{ background: RENK.ozelGun, padding: '0 4px' }}>sarı sütun</span> özel gün · <span style={{ background: RENK.haftaSonu, padding: '0 4px' }}>gri sütun</span> hafta sonu.
                            {' '}Ek · Diğer: ofis ve araç QR'ıyla yapılan personel ek mesaisi (müşteriye gitmez, ücreti ödenir).
                            {' '}Puantaj fatura atamasından bağımsızdır; havuzda bekleyen ek mesai de burada görünür. O ay kaydı olan bütün personel listelenir; yalnız sürücü kayıtları puantajda yer almaz.
                            {ucretVar && ' Ek mesai ücreti personelin alacağıdır: ek mesai × saatlik ücret × işletme ek mesai çarpanı (Çalışma Saatleri).'}
                        </div>
                    </KTCardBody>
                </KTCard>
            </Content>
        </React.Fragment>
    )
}

export default Puantaj;
